import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import { ArrowBackIosNewRounded, CloseRounded } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from '../../store/hooks'
import ExcelStep1 from '../../assets/datauploadsteps/windows/excel/step1.png'
import ExcelStep2 from '../../assets/datauploadsteps/windows/excel/step2.png'
import ExcelStep3 from '../../assets/datauploadsteps/windows/excel/step3.png'
import ExcelStep4 from '../../assets/datauploadsteps/windows/excel/step4.png'
import NumbersStep1 from '../../assets/datauploadsteps/mac/numbers/step1.png'
import NumbersStep2 from '../../assets/datauploadsteps/mac/numbers/step2.png'
import OpenOfficeStep1 from '../../assets/datauploadsteps/mac/openoffice/step1.png'
import NotepadStep1 from '../../assets/datauploadsteps/windows/notepad/step1.png'
import Template from '../../assets/datauploadsteps/template.png'
import { getIcon } from '../../components/common/Icons'

const PtDataUploadStepsView = () => {
  const { t } = useTranslation()
  const { currentLanguage } = useAppSelector((state) => state.currentUser)
  const [currentSoftware, setCurrentSoftware] = useState<string | null>(null)
  const navigate = useNavigate()

  return (
    <Box>
      <Stack direction="row" alignItems="center" mb={2}>
        <Box mt={1}>{getIcon('etl')}</Box>
        <Typography variant="h1" ml={1} pr={2} mt={0.5}>
          {t('upload.data')}
        </Typography>
      </Stack>
      <Button
        variant="text"
        startIcon={<ArrowBackIosNewRounded />}
        onClick={() => {
          navigate('/pt/upload-files')
        }}
      >
        {t('back')}
      </Button>

      <Box sx={{ height: 'calc(100vh - 139px)', overflowY: 'auto', textAlign: 'justify', pb: 6 }}>
        <Typography variant="h2" my={2}>
          {t('info')}
        </Typography>
        <Box>
          <Typography variant="h2" fontWeight="bold">
            {t('how.to')}
          </Typography>
          <List sx={{ listStyle: 'decimal', pl: 2.5 }}>
            <ListItem sx={{ display: 'list-item' }}>
              <ListItemText>
                <Typography>
                  {t('download.template')}{' '}
                  <Link href={`${process.env.REACT_APP_URL}/static/PT_data_upload_template.csv`}>
                    {currentLanguage === 'de' ? 'hier' : 'here'}
                  </Link>
                  {currentLanguage === 'de' ? ' herunter' : ''}.
                </Typography>
              </ListItemText>
            </ListItem>
            <ListItem sx={{ display: 'list-item' }}>
              <ListItemText>
                <Typography>{t('import')}</Typography>
              </ListItemText>
            </ListItem>
            <List sx={{ listStyle: 'decimal', pl: 9 }}>
              <ListItem sx={{ display: 'list-item', listStyle: 'lower-alpha' }}>
                <Link onClick={() => setCurrentSoftware('Excel')} sx={{ cursor: 'pointer' }}>
                  <ListItemText>
                    <Typography fontWeight="semibold">Excel</Typography>
                  </ListItemText>
                </Link>
              </ListItem>
              <ListItem sx={{ display: 'list-item', listStyle: 'lower-alpha' }}>
                <Link onClick={() => setCurrentSoftware('Numbers')} sx={{ cursor: 'pointer' }}>
                  <ListItemText>
                    <Typography fontWeight="semibold">Numbers</Typography>
                  </ListItemText>
                </Link>
              </ListItem>
              <ListItem sx={{ display: 'list-item', listStyle: 'lower-alpha' }}>
                <Link onClick={() => setCurrentSoftware('OpenOffice')} sx={{ cursor: 'pointer' }}>
                  <ListItemText>
                    <Typography fontWeight="semibold">OpenOffice</Typography>
                  </ListItemText>
                </Link>
              </ListItem>
              <ListItem sx={{ display: 'list-item', listStyle: 'lower-alpha' }}>
                <Link onClick={() => setCurrentSoftware('Notepad++')} sx={{ cursor: 'pointer' }}>
                  <ListItemText>
                    <Typography fontWeight="semibold">Notepad++</Typography>
                  </ListItemText>
                </Link>
              </ListItem>
            </List>
          </List>
          <Divider />
          <Typography variant="h2" mt={4} mb={2} fontWeight="semibold">
            {t('column.mean')}
          </Typography>
          <Stack direction="row" alignItems="flex-end">
            <Typography variant="h2">{'\u2022'} Gender/Geschlecht</Typography>
          </Stack>
          <Typography>{t('gender.mean')}</Typography>
          <Stack direction="row" alignItems="flex-end" mt={4}>
            <Typography variant="h2">{'\u2022'} Indikator für gleichwertige Arbeit</Typography>
          </Stack>
          <Typography>{t('pt.assistance.tat.tooltip')}</Typography>
          <Stack direction="row" alignItems="flex-end" mt={4}>
            <Typography variant="h2">
              {'\u2022'} Bruttogrundentgelt als Vollzeitäquivalent
            </Typography>
          </Stack>
          <Typography component="div" style={{ whiteSpace: 'pre-line' }}>
            {t('pt.assistance.salary.tooltip')}
          </Typography>
          <Stack direction="row" alignItems="flex-end" mt={4}>
            <Typography variant="h2">
              {'\u2022'} Variable Entgeltbestandteile für individuelle Leistung
            </Typography>
            <Typography ml={1} mb={0.5}>
              ( Optional )
            </Typography>
          </Stack>
          <Typography component="div" style={{ whiteSpace: 'pre-line' }}>
            {t('variable_wage_individual.mean')}
          </Typography>
          <Stack direction="row" alignItems="flex-end" mt={4}>
            <Typography variant="h2">
              {'\u2022'} Variable Entgeltbestandteile, die vom Unternehmenserfolg abhängig sind
            </Typography>
            <Typography ml={1} mb={0.5}>
              ( Optional )
            </Typography>
          </Stack>
          <Typography component="div" style={{ whiteSpace: 'pre-line' }}>
            {t('variable_wage_company.mean')}
          </Typography>
          <Stack direction="row" alignItems="flex-end" mt={4}>
            <Typography variant="h2">{'\u2022'} Überstundenentgelt</Typography>
            <Typography ml={1} mb={0.5}>
              ( Optional )
            </Typography>
          </Stack>
          <Typography component="div" style={{ whiteSpace: 'pre-line' }}>
            {t('variable_wage_overtime.mean')}
          </Typography>
          <Stack direction="row" alignItems="flex-end" mt={4}>
            <Typography variant="h2">
              {'\u2022'} Zuschläge für Schicht-, Samstags-, Sonntags-, Feiertags- oder Nachtarbeit
            </Typography>
            <Typography ml={1} mb={0.5}>
              ( Optional )
            </Typography>
          </Stack>
          <Typography component="div" style={{ whiteSpace: 'pre-line' }}>
            {t('surcharge_work_shift_overtime_night_publicholidays.mean')}
          </Typography>
          <Stack direction="row" alignItems="flex-end" mt={4}>
            <Typography variant="h2">{'\u2022'} Zulagen für Erschwernisse</Typography>
            <Typography ml={1} mb={0.5}>
              ( Optional )
            </Typography>
          </Stack>
          <Typography component="div" style={{ whiteSpace: 'pre-line' }} mb={4}>
            {t('surcharge_work_hardship.mean')}
          </Typography>
          <Divider />
          <Typography variant="h2" mt={4} fontWeight="bold">
            {t('after.title')}
          </Typography>
          <Typography my={2}>{t('template')}</Typography>
          <img src={Template} alt="Template" style={{ height: 135 }} />
          <Typography component="div" sx={{ my: 4, fontWeight: 'bold' }}>
            {currentLanguage === 'de'
              ? 'Noch Fragen? Bitte kontaktieren Sie uns unter'
              : 'More questions? Please contact us at'}{' '}
            <Link ml={0.5} href="mailto:support.pt@ines-analytics.com">
              support.pt@ines-analytics.com
            </Link>
          </Typography>
        </Box>
        <Dialog
          open={!!currentSoftware}
          onClose={() => setCurrentSoftware(null)}
          aria-labelledby="responsive-dialog-title-results"
          sx={{
            '& .MuiDialog-container': {
              '& .MuiPaper-root': {
                width: '100%',
                maxWidth: '850px',
              },
            },
            zIndex: 2000,
          }}
        >
          <DialogTitle id="responsive-dialog-title-results">{currentSoftware}</DialogTitle>
          <IconButton
            aria-label="close"
            onClick={() => setCurrentSoftware(null)}
            sx={(theme) => ({
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            })}
          >
            <CloseRounded />
          </IconButton>
          <DialogContent>
            {currentSoftware === 'Excel' && (
              <>
                <Typography variant="h2">{t('excel.step.1')}</Typography>
                <List>
                  <ListItem sx={{ display: 'list-item' }}>
                    <ListItemText>
                      <Typography fontWeight="semibold">{t('excel.step.11')}</Typography>
                    </ListItemText>
                    <img src={ExcelStep1} alt="ExcelStep1" style={{ width: 700 }} />
                    <ListItemText>
                      <Typography mt={4} fontWeight="semibold">
                        {t('excel.step.2')}
                      </Typography>
                    </ListItemText>
                    <img src={ExcelStep2} alt="ExcelStep2" style={{ width: 700 }} />
                    <ListItemText>
                      <Typography mt={4} fontWeight="semibold">
                        {t('excel.step.3')}
                      </Typography>
                    </ListItemText>
                    <img src={ExcelStep3} alt="ExcelStep3" style={{ width: 700 }} />
                    <ListItemText>
                      <Typography mt={4} fontWeight="semibold">
                        {t('excel.step.4')}
                      </Typography>
                    </ListItemText>
                    <img src={ExcelStep4} alt="ExcelStep4" style={{ width: 700 }} />
                  </ListItem>
                </List>
              </>
            )}
            {currentSoftware === 'Numbers' && (
              <List>
                <ListItem sx={{ display: 'list-item' }}>
                  <ListItemText>
                    <Typography fontWeight="semibold">{t('numbers.step.1')}</Typography>
                  </ListItemText>
                  <img src={NumbersStep1} alt="NumbersStep1" style={{ width: 700 }} />
                  <ListItemText>
                    <Typography mt={4} fontWeight="semibold">
                      {t('numbers.step.2')}
                    </Typography>
                  </ListItemText>
                  <img src={NumbersStep2} alt="NumbersStep2" style={{ width: 700 }} />
                </ListItem>
              </List>
            )}
            {currentSoftware === 'OpenOffice' && (
              <List>
                <ListItem sx={{ display: 'list-item' }}>
                  <ListItemText>
                    <Typography fontWeight="semibold">{t('open.step.1')}</Typography>
                  </ListItemText>
                  <img src={OpenOfficeStep1} alt="OpenOfficeStep1" style={{ width: 700 }} />
                </ListItem>
              </List>
            )}
            {currentSoftware === 'Notepad++' && (
              <List>
                <ListItem sx={{ display: 'list-item' }}>
                  <ListItemText>
                    <Typography fontWeight="semibold">{t('note.step.1')}</Typography>
                  </ListItemText>
                  <img src={NotepadStep1} alt="NotepadStep1" style={{ width: 700 }} />
                </ListItem>
              </List>
            )}
          </DialogContent>
          <DialogActions sx={{ m: 4 }}>
            <Button variant="contained" onClick={() => setCurrentSoftware(null)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  )
}

export default PtDataUploadStepsView
